import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';

const ThankYouPage = () => {
  const location = useLocation();
  const [donationInfo, setDonationInfo] = useState({
    amount: 0,
    name: '',
    email: ''
  });

  useEffect(() => {
    // Get donation info from URL params or localStorage
    const params = new URLSearchParams(location.search);
    const amount = params.get('amount') || localStorage.getItem('lastDonationAmount') || 0;
    const name = params.get('name') || localStorage.getItem('lastDonationName') || '';
    const email = params.get('email') || localStorage.getItem('lastDonationEmail') || '';

    setDonationInfo({ amount, name, email });
  }, [location]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #fff7ed 100%)',
      padding: 'clamp(1rem, 3vw, 2rem)',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto',
        textAlign: 'center',
        padding: '0 clamp(0.5rem, 2vw, 1rem)'
      }}>
        
        {/* Success Animation Container */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: 'clamp(1.5rem, 3vw, 2rem)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid #f3f4f6',
          padding: 'clamp(2rem, 5vw, 3rem)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          
          {/* Decorative Background Elements */}
          <div style={{
            position: 'absolute',
            top: '-50px',
            right: '-50px',
            width: '100px',
            height: '100px',
            background: 'linear-gradient(45deg, #C5EDDA, #bae6fd)',
            borderRadius: '50%',
            opacity: '0.1',
            animation: 'float 6s ease-in-out infinite'
          }}></div>
          
          <div style={{
            position: 'absolute',
            bottom: '-30px',
            left: '-30px',
            width: '60px',
            height: '60px',
            background: 'linear-gradient(45deg, #bae6fd, #C5EDDA)',
            borderRadius: '50%',
            opacity: '0.1',
            animation: 'float 8s ease-in-out infinite reverse'
          }}></div>

          {/* Success Icon */}
          <div style={{
            width: 'clamp(4rem, 10vw, 6rem)',
            height: 'clamp(4rem, 10vw, 6rem)',
            background: 'linear-gradient(135deg, #10b981, #059669)',
            borderRadius: '50%',
            margin: '0 auto clamp(1rem, 3vw, 1.5rem)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 20px 25px -5px rgba(16, 185, 129, 0.3)',
            animation: 'bounce 1s ease-in-out'
          }}>
            <svg 
              style={{ 
                width: 'clamp(2rem, 5vw, 3rem)', 
                height: 'clamp(2rem, 5vw, 3rem)',
                color: 'white'
              }} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={3} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>

          {/* Thank You Message */}
          <h1 style={{
            fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
            fontWeight: '800',
            color: '#111827',
            marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
            lineHeight: '1.2'
          }}>
            Thank You! 🎉
          </h1>
          
          <p style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
            color: '#4b5563',
            marginBottom: 'clamp(1rem, 3vw, 1.5rem)',
            lineHeight: '1.6'
          }}>
            Your generous donation of{' '}
            <span style={{
              color: '#059669',
              fontWeight: '700',
              fontSize: 'clamp(1.125rem, 3vw, 1.25rem)'
            }}>
              ${donationInfo.amount}
            </span>
            {' '}has been received and will make a real difference in the lives of refugees and communities in need.
          </p>

          {/* Impact Message */}
          <div style={{
            background: 'linear-gradient(135deg, #f0f9ff, #ecfdf5)',
            borderRadius: 'clamp(0.75rem, 2vw, 1rem)',
            padding: 'clamp(1rem, 3vw, 1.5rem)',
            marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
            border: '1px solid #e0f2fe'
          }}>
            <h3 style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
              fontWeight: '600',
              color: '#0369a1',
              marginBottom: '0.5rem'
            }}>
              🌟 Your Impact
            </h3>
            <p style={{
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              color: '#0c4a6e',
              margin: '0',
              lineHeight: '1.5'
            }}>
              With your support, we can continue providing essential services, education, and hope to those who need it most. Every dollar counts!
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'clamp(0.75rem, 2vw, 1rem)',
            alignItems: 'center'
          }}>
            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.5rem, 4vw, 2rem)',
                background: 'linear-gradient(to right, #2E8B54, #1B2B5C)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '0.75rem',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                fontSize: 'clamp(0.875rem, 2.5vw, 1rem)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
              }}
            >
              <svg style={{ width: 'clamp(1rem, 2.5vw, 1.25rem)', height: 'clamp(1rem, 2.5vw, 1.25rem)', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Return to Home
            </Link>

            <Link
              to="/donate"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 3vw, 1.5rem)',
                background: 'transparent',
                color: '#6b7280',
                fontWeight: '500',
                borderRadius: '0.5rem',
                textDecoration: 'none',
                transition: 'all 0.2s ease',
                border: '1px solid #d1d5db',
                fontSize: 'clamp(0.75rem, 2vw, 0.875rem)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#d1d5db';
              }}
            >
              <svg style={{ width: 'clamp(0.875rem, 2vw, 1rem)', height: 'clamp(0.875rem, 2vw, 1rem)', marginRight: '0.5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Make Another Donation
            </Link>
          </div>
        </div>

        {/* Footer Message */}
        <p style={{
          fontSize: 'clamp(0.75rem, 2vw, 0.875rem)',
          color: '#6b7280',
          marginTop: 'clamp(1rem, 3vw, 1.5rem)',
          textAlign: 'center'
        }}>
          💝 From the bottom of our hearts, thank you for your generosity and support!
        </p>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0,0,0);
          }
          40%, 43% {
            transform: translate3d(0,-30px,0);
          }
          70% {
            transform: translate3d(0,-15px,0);
          }
          90% {
            transform: translate3d(0,-4px,0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
};

export default ThankYouPage;
